import { create } from '@bufbuild/protobuf'
import { localStore } from 'utils/storage'
import { GetBalanceResponseSchema } from '../gen/service/creativefabrica/coins/v2/service_pb'
import * as React from 'react'
import { useNotifications } from '@repo/notifications/use-notifications'
import type { NotificationPayload } from '@repo/notifications/use-notifications'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useWorkspaceQuery } from '@repo/studio-hooks-auth/use-workspace-query'
import { getToken } from '@repo/rpc-codegen/fetch-rpc'
import { getIsLoggedIn } from 'auth/fetchers'
import { useIsLoggedInQuery, useUserIdQuery } from 'auth/hooks'
import { queryClient } from '@repo/studio-utils/query-client'
import type { GetBalanceResponse } from '../gen/service/creativefabrica/coins/v2/service_pb'
import { getBalanceQueryOptions, BALANCE_QUERY_VARIABLES } from './coin-balance-query'
import { applyBalanceUpdatedNotification } from './apply-balance-updated-notification'

// Query-key helpers consumers need to read/prefetch the cached wallet (e.g. CoinBalance
// suspense + lumo BusinessLogicStore). Surfaced here so callers go through this module
// rather than reaching into gen/ across the package boundary.
// oxlint-disable-next-line cf-web/no-barrel-file
export { getBalanceQueryKey, getBalanceQueryOptions } from './coin-balance-query'

export function getCoinBalanceNumberQueryOptions(workspaceId: string | null) {
  const { queryKey, queryFn } = getBalanceQueryOptions(BALANCE_QUERY_VARIABLES, workspaceId)
  if (typeof queryFn !== 'function') {
    throw new Error('GetBalance queryFn is required')
  }
  return {
    queryKey,
    queryFn,
    select: (data: GetBalanceResponse) => Number(data.balance),
  }
}

const BALANCE_UPDATED_EVENT = {
  coins: {
    balanceUpdated: {},
  },
}

export function useCoinBalanceUpdatedSubscription() {
  const queryClientFromContext = useQueryClient()
  const { data: userId } = useUserIdQuery()
  const { data: workspaceId } = useWorkspaceQuery()

  const onNotification = React.useCallback(
    (notification: NotificationPayload) => {
      applyBalanceUpdatedNotification(queryClientFromContext, notification, userId, workspaceId)
    },
    [queryClientFromContext, userId, workspaceId],
  )

  useNotifications({
    event: BALANCE_UPDATED_EVENT,
    onNotification,
    enabled: Boolean(userId) && workspaceId !== undefined,
  })

  return workspaceId
}

export function useCoinBalanceQuery() {
  const workspaceId = useCoinBalanceUpdatedSubscription()
  const { data: isLoggedIn } = useIsLoggedInQuery()

  return useQuery({
    ...getCoinBalanceNumberQueryOptions(workspaceId),
    enabled: workspaceId !== undefined && Boolean(isLoggedIn),
    placeholderData: workspaceId === null && isLoggedIn ? readPersonalCoinBalance() : undefined,
    refetchOnReconnect: true,
  })
}

/** @deprecated Prefer `useCoinBalanceQuery` — kept during GraphQL → GetBalance migration. */
export const useCoinBalance = useCoinBalanceQuery

export async function prefetchCoinBalanceQuery(workspaceId: string | null) {
  const isLoggedIn = await getIsLoggedIn(queryClient)
  if (!isLoggedIn) {
    return
  }
  await getToken()
  await queryClient.prefetchQuery(getBalanceQueryOptions(BALANCE_QUERY_VARIABLES, workspaceId))
}

const PERSONAL_BALANCE_STORAGE_KEY = 'studio:last-coin-balance'

function readPersonalCoinBalance() {
  try {
    const balance = localStore.getString(PERSONAL_BALANCE_STORAGE_KEY)
    return balance ? create(GetBalanceResponseSchema, { balance: BigInt(balance) }) : undefined
  } catch {
    return undefined
  }
}

export function saveCoinBalanceToStorage(balance: number) {
  try {
    localStore.setString(PERSONAL_BALANCE_STORAGE_KEY, balance.toString())
  } catch {}
}
